import { Controller } from '@hotwired/stimulus'
// @ts-expect-error: @hotwired/turbo is not typed
import * as Turbo from '@hotwired/turbo'
// @ts-expect-error: el-transition is not typed
import { enter, leave } from 'el-transition'

import { membersCards } from 'rails_routes'

// VGSCollect comes from https://js.verygoodvault.com/vgs-collect/2.18.5/vgs-collect.js
// which is loaded via `javascript_include_tag`, which makes it available as
// `window.VGSCollect`.
declare global {
  interface Window {
    VGSCollect: {
      create(vaultId: string, environment: string, stateCallback?: (state: any) => void): any;
    };
  }
}

// Connects to data-controller="vgs-collect-cvv2"
export default class extends Controller {
  static targets = [
    'body',
    'close',
  ]

  declare readonly bodyTarget: HTMLElement
  declare readonly closeTarget: HTMLElement

  static values = {
    cardId: String,
    cardRemoteId: String,
    customerToken: String,
    vgsEnv: String,
    vgsVaultId: String,
  }

  declare vgsVaultIdValue: string
  declare vgsEnvValue: string
  declare customerTokenValue: string
  declare cardIdValue: string
  declare cardRemoteIdValue: string

  connect () {
    enter(this.closeTarget)
    enter(this.bodyTarget)

    document.body.classList.add('modal-open') // Locks scrolling

    const vgsVaultId = this.vgsVaultIdValue
    const vgsEnv = this.vgsEnvValue
    const customerToken = this.customerTokenValue
    const cardId = this.cardIdValue
    const cardRemoteId = this.cardRemoteIdValue

    const ccInputCSS = {
      'font-family': 'Inter var, ui-sans-serif, system-ui',
      'font-size': '14px',
      display: 'block',
      width: '80%',
      height: '90%',
      '-webkit-appearance': 'none',
      '-moz-appearance': 'none',
      appearance: 'none',
      border: '1px solid transparent',
      'border-radius': '0.375rem',
      'padding-left': '10px',
      filter: 'drop-shadow(0 1px 1px rgb(0 0 0 / 0.05))',
      '&:focus': {
        'border-width': '2px',
        'border-color': '#6366f1',
        outline: '2px solid transparent',
        'outline-offset': '2px',
      },
    }

    const form = window.VGSCollect.create(vgsVaultId, vgsEnv, function (state: any) {
      if (state) {
        for (const field in state) {
          const span: HTMLElement = document.querySelector(`[data-name='${field}']`)!
          const frame = span.previousElementSibling!
          if (
            state[field].errorMessages &&
            !state[field].isValid &&
            !state[field].isFocused &&
            state[field].isDirty
          ) {
            const error = state[field].errorMessages[0]
            span.innerText = error
            if (!frame.classList.contains('invalid-field')) {
              frame.classList.add('invalid-field')
            }
          } else if (state[field].isValid || state[field].isFocused) {
            span.innerText = ''
            frame.classList.remove('invalid-field')
          }
        }
      }
    })

    form.field('#cc-cvv2', {
      type: 'card-security-code',
      name: 'data.attributes.cvv2',
      successColor: '#4F8A10',
      errorColor: '#D8000C',
      placeholder: '123',
      maxLength: 3,
      autoFocus: true,
      validations: ['required', 'validCardSecurityCode'],
      css: ccInputCSS,
    })

    form.field('#cc-expiration-date', {
      type: 'card-expiration-date',
      name: 'data.attributes.expirationDate',
      successColor: '#4F8A10',
      errorColor: '#D8000C',
      placeholder: 'MM / YYYY',
      validations: ['required', 'validCardExpirationDate'],
      // Change to required expiration date format
      serializers: [form.SERIALIZERS.replace('(\\d{2})\\s\\/\\s(\\d{2,4})', '$2-$1')],
      css: ccInputCSS,
    })

    const highlightErrors = function (targetForm: HTMLFormElement, errors: any[]) {
      targetForm
        .querySelectorAll('.vgs-collect-container__invalid')
        .forEach(e => e.classList.add('invalid-field'))
      for (const error in errors) {
        const errorMessage = errors[error].errorMessages[0]
        const span = targetForm.querySelector('[data-name="' + error + '"]')! as HTMLSpanElement
        if (!span.innerText) {
          span.innerText = errorMessage
        }
      }

      enableSubmit()
    }

    const submitForm = function (form: any, targetForm: HTMLFormElement) {
      form.submit(
        // *** IMPORTANT *** THIS IS A UNIT ROUTE, NOT A THATCH ROUTE!
        // This relative link lives insides the Unit modal for invoking the card activation flow.
        // The operation will fail AND CAUSE AN INCIDENT if you change it to a Thatch-prefixed route!
        '/cards/' + cardRemoteId + '/activate',
        {
          // This converts the dot-separated field name strings into a JSON object
          mapDotToObject: 'true',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            Authorization: 'Bearer ' + customerToken,
          },
        },
        function (status: number, data: any) {
          if (status >= 200 && status < 300) {
            // Success
            // Manually set the Turbo visit because if the activation was from the redirect of /members/cards/activate
            // then the window pathname will not have the card id since we're assuming that the member just
            // wants to activate their current physical card vs. us seeing the card in question via the URL
            document.body.classList.remove('modal-open') // Unlocks scrolling
            Turbo.visit(membersCards.setPin({ card_id: cardId }), { frame: 'modal', action: 'advance' })
          } else {
            // TODO: Replace alert with an actually nice way of displaying errors from Unit's API.
            alert(data.errors[0].title)
            enableSubmit()
          }
        },
        function (errors: any[]) {
          highlightErrors(targetForm, errors)
        }
      )
    }

    const disableSubmit = function () {
      const submitButton = document.getElementById('activate-submit') as HTMLButtonElement
      submitButton.disabled = true
      submitButton.textContent = 'Activating...'
    }

    const enableSubmit = function () {
      const submitButton = document.getElementById('activate-submit') as HTMLButtonElement
      submitButton.disabled = false
      submitButton.textContent = 'Activate'
    }

    // Submit by executing a POST request.
    document.getElementById('cc-form')!
      .addEventListener('submit', function (e) {
        e.preventDefault()
        const targetForm = e.target as HTMLFormElement
        disableSubmit()
        submitForm(form, targetForm)
      }, false)
  }

  close () {
    Promise.all([leave(this.closeTarget), leave(this.bodyTarget)]).then(() => {
      document.body.classList.remove('modal-open') // Unlocks scrolling
      Turbo.visit(membersCards.index(), { frame: 'modal' })
    })
  }
}
